/**
 * Handles simple notification sounds for the app.
 * ## Usage
 * - Use function `playNotificationSound(type)` to play a sound.
 *
 * ## Adding Sounds
 * 1. Add the audio file to `/assets/audio/`.
 * 2. Create an `Audio` object here and set its volume. ( in case a new sound is way louder or quieter than the others )
 * 3. Update `NotificationType` and `audioByType` with the new sound.
 */
import { getLogger } from "@expert/logging";

const logger = getLogger({ module: "ChatNotificationSounds" });

// BEGIN AUDIO FILES
const newMessageAudio = new Audio("/assets/audio/new-msg-alert.wav");
newMessageAudio.volume = 0.6;

const newChatAudio = new Audio("/assets/audio/new-customer-arrives.wav");
newChatAudio.volume = 0.5;

const callDisconnectedAudio = new Audio("/assets/audio/call-disconnected.mp3");
callDisconnectedAudio.volume = 0.6;

const warmHandoffConnectingAudio = new Audio("/assets/audio/warm-handoff-connecting.mp3");
warmHandoffConnectingAudio.volume = 0.6;

const warmHandoffLiveAudio = new Audio("/assets/audio/warm-handoff-live.mp3");
warmHandoffLiveAudio.volume = 0.6;
// END AUDIO FILES

type NotificationType = "newMessage" | "newChat" | "callDisconnected" | "warmHandoffConnecting" | "warmHandoffLive";

/** Maps notification types to their corresponding audio elements */
const audioByType: Record<NotificationType, HTMLAudioElement> = {
    newMessage: newMessageAudio,
    newChat: newChatAudio,
    callDisconnected: callDisconnectedAudio,
    warmHandoffConnecting: warmHandoffConnectingAudio,
    warmHandoffLive: warmHandoffLiveAudio,
};

/**
 * Plays a notification sound based on the provided notification type.
 *
 * @param type - The type of notification sound to play.
 *               ie: `"newMessage"` for a new message alert,
 *               `"newChat"` for a new customer arrival alert,
 *               or `"callDisconnected"` for an unexpected voice call drop
 * @returns A promise that resolves when the audio STARTS playing
 *
 * If the audio fails to play, a warning is logged using the logger instead of throwing an error
 */
export const playNotificationSound = async (type: NotificationType): Promise<boolean> => {
    try {
        await audioByType[type].play();
        return true;
    } catch (error) {
        logger.warn(error, `Failed to play ${type} sound`);
        return false;
    }
};
